import { Capabilities, VersionEstimate } from "types/analytics";

/**
 * Brackets the Chromium version from observed feature support, without trusting
 * the user agent string at all.
 *
 * This is the fallback that matters on plotters: several marine displays ship a
 * WebView with a rewritten, frozen or model-only user agent, and Chromium itself
 * now reduces the version it reports. Features that are present put a floor
 * under the version; features that are absent put a ceiling over it.
 *
 * Only CSS and DOM landmarks are used. ES built-ins are deliberately excluded
 * because `core-js` is imported at startup and backfills them on old engines,
 * which would make an ancient WebView look modern.
 */

interface Landmark {
  /** The Chromium version this feature shipped in */
  version: number;
  name: string;
  supported: (capabilities: Capabilities) => boolean | null;
}

const LANDMARKS: Landmark[] = [
  {
    version: 49,
    name: "css-custom-properties",
    supported: (c) => c.css.custom_properties,
  },
  {
    version: 51,
    name: "intersection-observer",
    supported: (c) => c.js.intersection_observer,
  },
  { version: 53, name: "shadow-dom-v1", supported: (c) => c.js.shadow_dom },
  {
    version: 54,
    name: "custom-elements-v1",
    supported: (c) => c.js.custom_elements,
  },
  {
    version: 56,
    name: "css-position-sticky",
    supported: (c) => c.css.position_sticky,
  },
  { version: 57, name: "css-grid", supported: (c) => c.css.grid },
  {
    version: 63,
    name: "css-overscroll-behavior",
    supported: (c) => c.css.overscroll_behavior,
  },
  {
    version: 64,
    name: "resize-observer",
    supported: (c) => c.js.resize_observer,
  },
  { version: 66, name: "css-gap", supported: (c) => c.css.gap_property },
  {
    version: 69,
    name: "css-conic-gradient",
    supported: (c) => c.css.conic_gradient,
  },
  {
    version: 76,
    name: "css-backdrop-filter",
    supported: (c) => c.css.backdrop_filter,
  },
  { version: 79, name: "css-clamp", supported: (c) => c.css.min_max_clamp },
  { version: 84, name: "css-flex-gap", supported: (c) => c.css.flex_gap },
  {
    version: 87,
    name: "css-logical-properties",
    supported: (c) => c.css.logical_properties,
  },
  { version: 88, name: "css-is-selector", supported: (c) => c.css.is_selector },
  {
    version: 88,
    name: "css-aspect-ratio",
    supported: (c) => c.css.aspect_ratio,
  },
  {
    version: 88,
    name: "css-not-selector-list",
    supported: (c) => c.css.not_selector_list,
  },
  {
    version: 105,
    name: "css-has-selector",
    supported: (c) => c.css.has_selector,
  },
  {
    version: 105,
    name: "css-container-queries",
    supported: (c) => c.css.container_queries,
  },
];

export const estimateChromiumVersion = (
  capabilities: Capabilities
): VersionEstimate => {
  const estimate: VersionEstimate = {
    min_version: null,
    min_version_feature: null,
    max_version: null,
    max_version_feature: null,
  };

  for (let i = 0; i < LANDMARKS.length; i++) {
    const landmark = LANDMARKS[i];
    const supported = landmark.supported(capabilities);

    // Only a definite true/false is evidence. A probe that could not run (null)
    // or a field that is absent tells us nothing in either direction, and must
    // never be mistaken for "not supported".
    if (supported !== true && supported !== false) continue;

    if (supported) {
      // Present: the engine is at least this new
      if (
        estimate.min_version === null ||
        landmark.version > estimate.min_version
      ) {
        estimate.min_version = landmark.version;
        estimate.min_version_feature = landmark.name;
      }
    } else if (
      // Absent: the engine is older than this. Keep the lowest such version.
      estimate.max_version === null ||
      landmark.version < estimate.max_version
    ) {
      estimate.max_version = landmark.version;
      estimate.max_version_feature = landmark.name;
    }
  }

  return estimate;
};
